'use client';

import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { ArrowRight } from 'lucide-react';
import { SiteNav } from '@/components/SiteNav';
import { Reveal, FeatureRow, MarketingFooter, ShotStockTable, ShotCatalog, ShotTeam, ShotSettings } from '@/components/marketing';

export default function FeaturesContent() {
  const t = useTranslations('Landing');
  const tF = useTranslations('Features.hero');

  return (
    <div className="flex flex-col bg-stone-50 dark:bg-zinc-950 text-stone-950 dark:text-zinc-50">
      <SiteNav />

      {/* Hero */}
      <section style={{ padding: 'clamp(64px,9vw,120px) 0 clamp(32px,4vw,56px)' }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div
              className="font-mono text-[12px] font-medium tracking-[0.14em] uppercase mb-4"
              style={{ color: 'var(--accent)' }}
            >
              {tF('eyebrow')}
            </div>
            <h1
              className="font-black tracking-[-0.035em] leading-[1.05] m-0 mb-6 max-w-[20ch]"
              style={{ fontSize: 'clamp(40px,6vw,72px)' }}
            >
              {tF('title')}
            </h1>
            <p className="text-[18px] leading-[1.6] text-stone-500 dark:text-zinc-400 max-w-[50ch] m-0 mb-8">
              {tF('body')}
            </p>
            <Link
              href="/start"
              className="inline-flex items-center gap-2 px-6 py-[14px] text-[15px] font-semibold rounded-[10px] bg-stone-950 text-stone-50 dark:bg-zinc-50 dark:text-zinc-950 hover:opacity-90 transition-opacity"
            >
              {tF('cta')} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Feature rows */}
      <FeatureRow
        num="01"
        label={t('featureStock.label')}
        title={t('featureStock.title')}
        body={t('featureStock.body')}
        bullets={[t('featureStock.b1'), t('featureStock.b2'), t('featureStock.b3'), t('featureStock.b4')]}
        shot={<ShotStockTable />}
        id="stock"
      />
      <FeatureRow
        num="02"
        label={t('featureCatalog.label')}
        title={t('featureCatalog.title')}
        body={t('featureCatalog.body')}
        bullets={[t('featureCatalog.b1'), t('featureCatalog.b2'), t('featureCatalog.b3'), t('featureCatalog.b4')]}
        shot={<ShotCatalog />}
        flip
        id="catalog"
      />
      <FeatureRow
        num="03"
        label={t('featureTeam.label')}
        title={t('featureTeam.title')}
        body={t('featureTeam.body')}
        bullets={[t('featureTeam.b1'), t('featureTeam.b2'), t('featureTeam.b3')]}
        shot={<ShotTeam />}
        id="team"
      />
      <FeatureRow
        num="04"
        label={t('featureSettings.label')}
        title={t('featureSettings.title')}
        body={t('featureSettings.body')}
        bullets={[t('featureSettings.b1'), t('featureSettings.b2'), t('featureSettings.b3'), t('featureSettings.b4')]}
        shot={<ShotSettings />}
        flip
        id="settings"
      />

      {/* CTA */}
      <section style={{ padding: 'clamp(64px,9vw,128px) 0' }}>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="rounded-[20px] bg-stone-950 dark:bg-zinc-50 px-8 py-10 md:px-12 md:py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <h2 className="text-[clamp(24px,3vw,36px)] font-bold tracking-tight leading-[1.15] text-stone-50 dark:text-zinc-950 m-0 mb-2">
                  {t('cta.title')}
                </h2>
                <p className="text-[16px] text-stone-400 dark:text-zinc-500 m-0 max-w-[48ch]">
                  {t('cta.body')}
                </p>
              </div>
              <Link
                href="/start"
                className="shrink-0 inline-flex items-center gap-2 px-6 py-[14px] text-[15px] font-semibold rounded-[10px] bg-stone-50 text-stone-950 dark:bg-zinc-950 dark:text-zinc-50 hover:opacity-90 transition-opacity whitespace-nowrap"
              >
                {t('cta.btn')} <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
